import type { PricingCardPlan } from '@pray/shared/components/ui/PricingCard/PricingCard';
import { formatNumber, formatNumberCount } from '@pray/shared/utils';

import { CREDIT_RATES } from './creditRates';
import { type Plan, RecurringPeriod } from './types';

const planCredits = {
  basic: 1000,
  plus: 2000,
  premium: 0, // Enterprise -> 'Contact Sales'
};

// What a plan's monthly credits buy when spent on a single feature.
const planCreditFeatures = (credits: number): string[] => [
  `${formatNumber((CREDIT_RATES.audioTranslationMinutes * credits) / 60)} hours of audio translations`,
  `${formatNumber(Math.floor((CREDIT_RATES.videoTranslationSeconds * credits) / 60))} mins of video translations`,
  `${formatNumber(CREDIT_RATES.socialClipsVideoMinutes * credits)} mins of video for social clips`,
  `${formatNumberCount(CREDIT_RATES.emails * credits)} emails`,
];

export const plans: Plan[] = [
  {
    name: 'Basic',
    description: 'Perfect for small ministries',
    pricing: {
      monthly: {
        id: 'fedfb572-a0a3-11f0-a90b-0affd99fa7d3',
        sku: 'com.prayapp.studio.sub.monthly.100000',
        price: 100000,
        credits: planCredits.basic,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
      yearly: {
        id: '028a0fe6-a0a4-11f0-a90b-0affd99fa7d3',
        sku: 'com.prayapp.studio.sub.yearly.1080000',
        price: 90000,
        credits: planCredits.basic,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
    },
    isPopular: true,
    features: [
      ...planCreditFeatures(planCredits.basic),
      'Unlimited Team Members',
      'Unlimited Chat',
      'Unlimited Announcements*',
      'Unlimited Storage*',
      'Unlimited Streaming*',
    ],
  },
  {
    name: 'Plus',
    description: 'Perfect for growing ministries',
    pricing: {
      monthly: {
        id: '00f89af9-a0a4-11f0-a90b-0affd99fa7d3',
        sku: 'com.prayapp.studio.sub.monthly.200000',
        price: 200000,
        credits: planCredits.plus,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
      yearly: {
        id: '050b1bf7-a0a4-11f0-a90b-0affd99fa7d3',
        sku: 'com.prayapp.studio.sub.yearly.2160000',
        price: 180000,
        credits: planCredits.plus,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
    },
    isPopular: false,
    features: [
      ...planCreditFeatures(planCredits.plus),
      'Unlimited Team Members',
      'Unlimited Chat',
      'Unlimited Announcements*',
      'Unlimited Storage*',
      'Unlimited Streaming*',
    ],
  },
  {
    name: 'Enterprise',
    description: 'Perfect for large ministries',
    pricing: {
      monthly: {
        id: 'bfd85b87-ce32-4f89-8624-d322c062c608',
        sku: 'com.prayapp.studio.sub.monthly.49999',
        price: 0,
        credits: planCredits.premium,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
      yearly: {
        id: 'e6b02ef6-d957-4318-9245-58046df42798',
        sku: 'com.prayapp.studio.sub.yearly.539988',
        price: 0,
        credits: planCredits.premium,
        creditType: RecurringPeriod.MONTHLY,
        isFreeTrialEnabled: false,
      },
    },
    features: [
      `More hours of audio translations`,
      `More mins of video translations`,
      `More mins of video for social clips`,
      `More emails`,
      'Unlimited Team Members',
      'Unlimited Chat',
      'Unlimited Announcements*',
      'Unlimited Storage*',
      'Unlimited Streaming*',
    ],
  },
];

export const mapPlanToPricing = (plan: Plan, billingPeriod: RecurringPeriod): PricingCardPlan => {
  return {
    name: plan.name,
    description: plan.description,
    originalPrice: billingPeriod === RecurringPeriod.YEARLY ? plan.pricing.monthly.price : undefined,
    price: plan.pricing[billingPeriod].price,
    credits: plan.pricing[billingPeriod].credits,
    features: plan.features,
    isPopular: plan.isPopular,
    sku: plan.pricing[billingPeriod].sku,
    isFreeTrialEnabled: plan.pricing[billingPeriod].isFreeTrialEnabled,
    freeTrialInterval: plan.pricing[billingPeriod].freeTrialInterval,
    freeTrialIntervalCount: plan.pricing[billingPeriod].freeTrialIntervalCount,
    freeTrialCreditsAmount: plan.pricing[billingPeriod].freeTrialCreditsAmount,
  };
};

export type CheckoutPlan = {
  name: string;
  originalPrice?: number;
  price: number;
  credits: number;
  amount: number;
  recurringPeriod: RecurringPeriod;
  interval: 'month' | 'year';
};

export const mapPlanToCheckout = (plan: Plan, billingPeriod: RecurringPeriod): CheckoutPlan => {
  return {
    name: plan.name,
    originalPrice: billingPeriod === RecurringPeriod.YEARLY ? plan.pricing.monthly.price : undefined,
    price: plan.pricing[billingPeriod].price,
    credits: plan.pricing[billingPeriod].credits,
    recurringPeriod: billingPeriod,
    interval: billingPeriod === RecurringPeriod.YEARLY ? 'year' : 'month',
    // Checkout
    amount:
      (billingPeriod === RecurringPeriod.YEARLY
        ? plan.pricing[billingPeriod].price * 12
        : plan.pricing[billingPeriod].price) * 100,
  };
};
